import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    Optional,
    Self,
    ViewEncapsulation,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormGroup,
    NgControl,
} from '@angular/forms';
import {
    AssetScope,
    IAssetType,
} from '@models';

import isEqual from 'lodash-es/isEqual';

interface ICredentialValue {
    username: string;
    password: string;
}

export enum AssetType {
    Text = 'Text',
    Bool = 'Bool',
    Integer = 'Integer',
    Credential = 'Credential',
}

export type IAssetValue = ICredentialValue | boolean | string | number;

export const defaultValueFor = (type: IAssetType, base: IAssetValue | null = null, readonly?: boolean): IAssetValue | null => {
    switch (type) {
        case AssetType.Credential:
            if (readonly) {
                return base != null ?
                    base :
                    null;
            }
            base = base as ICredentialValue;
            return base &&
                (
                    base.username ||
                    base.password
                ) ?
                base : {
                    username: '',
                    password: '',
                };
        case AssetType.Bool:
            return base === false ||
                base === true ?
                base :
                false;
        default:
            return base != null ?
                base :
                null;
    }
};
@Component({
    selector: 'ui-value-type',
    templateUrl: './ui-value-type.component.html',
    styleUrls: ['./ui-value-type.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiValueTypeComponent implements ControlValueAccessor {
    public get ValueType() {
        return AssetType;
    }

    @Input()
    public valuePlaceholder = 'Value';
    @Input()
    public externalNamePlaceholder = 'External Name';
    @Input()
    public externalNamePlaceholderName = '';
    @Input()
    public usernamePlaceholder = 'Username';
    @Input()
    public passwordPlaceholder = 'Password';
    @Input()
    public truePlaceholder = 'True';
    @Input()
    public falsePlaceholder = 'False';
    @Input()
    public tooltipHide = 'Hide';
    @Input()
    public tooltipShow = 'Show';
    @Input()
    public form!: FormGroup;

    @Input()
    public isRobot?: boolean;

    @Input()
    public set readonlyCredential(value: boolean | undefined) {
        this._readonlyCredential = value;
        this._value = defaultValueFor(this._type, this._value, value)!;
    }
    public get readonlyCredential() {
        return this._readonlyCredential;
    }

    @Input()
    public set type(value: IAssetType) {
        this._value = defaultValueFor(value, this._value, this._readonlyCredential)!;
        this._type = value;
    }
    public get type() {
        return this._type;
    }

    @Input()
    public scope!: AssetScope;

    @Input()
    public disabled!: boolean;

    public set value(value: IAssetValue) {
        if (!isEqual(value, this._value)) {
            this._value = value;
            this.registerChange(value);
        }
    }
    public get value() {
        return this._value;
    }

    private _type!: IAssetType;
    private _value!: IAssetValue;
    private _readonlyCredential?: boolean;

    constructor(
        @Optional() @Self() public ngControl: NgControl,
        private _cd: ChangeDetectorRef,
    ) {
        // prevent cyclic dependency
        if (!!this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    public asCredential(value: IAssetValue) {
        return value as ICredentialValue;
    }

    public registerChange = (_: IAssetValue) => { };
    public registerTouch: (ev: Event) => void = (_: Event) => { };

    writeValue(value: IAssetValue) {
        if (!isEqual(value, this._value)) {
            this._value = value;
            this._cd.markForCheck();
        }
    }

    registerOnChange(fn: (_: IAssetValue) => {}) {
        this.registerChange = fn;
    }

    registerOnTouched(fn: (ev: Event) => void) {
        this.registerTouch = fn;
    }

    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
    }
}
